import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserPlus, UserMinus, Users, Search } from "lucide-react";

interface StaffMember {
  user_id: string;
  email: string;
  full_name: string | null;
  numeric_id: string | null;
}

const AdminStaff = () => {
  const { toast } = useToast();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [lookup, setLookup] = useState("");

  const fetchStaff = async () => {
    setLoading(true);
    const { data: roles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "staff" as any);

    const ids = (roles || []).map((r: any) => r.user_id);
    if (ids.length === 0) {
      setStaff([]);
      setLoading(false);
      return;
    }
    const { data: profs } = await supabase
      .from("profiles")
      .select("user_id, email, full_name, numeric_id")
      .in("user_id", ids);
    setStaff((profs || []) as StaffMember[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const addStaff = async () => {
    const query = lookup.trim();
    if (!query) return;
    setProcessing(true);
    try {
      // Lookup by numeric_id first, then email
      let profile: any = null;
      const { data: byNumeric } = await supabase
        .from("profiles")
        .select("user_id, email, full_name, numeric_id")
        .eq("numeric_id", query)
        .maybeSingle();
      profile = byNumeric;
      if (!profile) {
        const { data: byEmail } = await supabase
          .from("profiles")
          .select("user_id, email, full_name, numeric_id")
          .ilike("email", query)
          .maybeSingle();
        profile = byEmail;
      }
      if (!profile) {
        toast({ title: "لم يتم العثور على المستخدم", variant: "destructive" });
        return;
      }
      const { error } = await supabase.rpc("admin_set_staff" as any, {
        _target_user_id: profile.user_id,
        _is_staff: true,
      });
      if (error) throw error;
      toast({ title: "تم تعيين الموظف", description: profile.email });
      setLookup("");
      fetchStaff();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  const removeStaff = async (userId: string) => {
    if (!confirm("إزالة صلاحية الموظف؟")) return;
    setProcessing(true);
    try {
      const { error } = await supabase.rpc("admin_set_staff" as any, {
        _target_user_id: userId,
        _is_staff: false,
      });
      if (error) throw error;
      toast({ title: "تم رفع الصلاحية" });
      fetchStaff();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Users className="w-5 h-5 text-primary" />
          إدارة الموظفين ({staff.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="ابحث بمعرّف ID أو البريد الإلكتروني"
              className="pr-10 bg-secondary/60 border-0 rounded-xl"
            />
          </div>
          <Button onClick={addStaff} disabled={processing || !lookup.trim()}>
            <UserPlus className="w-4 h-4 ml-1" />
            تعيين
          </Button>
        </div>

        <div className="rounded-lg border border-border/40 bg-secondary/20 p-3 text-xs text-muted-foreground">
          صلاحيات الموظف: الرد على تذاكر الدعم الفني + قراءة السحوبات والإيداعات والمستخدمين (بدون موافقة/رفض).
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : staff.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground text-sm">لا يوجد موظفون معيّنون</p>
        ) : (
          <div className="space-y-2">
            {staff.map((s) => (
              <div
                key={s.user_id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary/40 border border-border/40"
              >
                <div>
                  <p className="text-foreground font-medium">{s.full_name || s.email}</p>
                  <p className="text-xs text-muted-foreground font-mono" dir="ltr">
                    ID: {s.numeric_id || "-"} · {s.email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/20 text-primary">موظف</Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeStaff(s.user_id)}
                    disabled={processing}
                  >
                    <UserMinus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminStaff;
